import {
  Matrix4,
  Mesh,
  Ray,
  Sphere,
  Vector3,
  type BatchedMesh,
  type BufferGeometry,
  type Intersection,
  type Material,
  type Raycaster,
} from "three";
import { BatchPartIndex } from "./batchPartIndex";

/**
 * Raycasting through a geometry's spatial index (`geometry.boundsTree`, built
 * off the main thread by `MeshBoundsTreeBuilder`).
 *
 * Three's own `Mesh.raycast` tests every triangle of every mesh whose bounds
 * the ray crosses. A pick runs on every pointer move, and Spark probes its own
 * visibility with rays four times a second, so on dense CAD that is most of a
 * frame: over Apollo 11 Hatch Door (3.57 M triangles) a pick cost 16 ms on
 * average and 65 ms at worst. Through the index it costs 0.3 ms, with the same
 * result for every ray (2026-10-07, 1 000 rays: same part, distance, point and
 * normal, and the same full hit lists including face and batch ids).
 *
 * Results match three's because the library's triangle test is three's own,
 * run on the same triangles: only the order of hits WITHIN one mesh differs,
 * and the raycaster sorts by distance afterwards.
 */

let s_stockMeshRaycast: Mesh["raycast"] | null = null;

const _sphere = new Sphere();
const _ray = new Ray();
const _sphereHit = new Vector3();

/**
 * Routes every `Mesh` raycast through {@link boundsTreeMeshRaycast}. Idempotent.
 * A mesh whose geometry has no tree is raycast by three's own code, unchanged,
 * so this is safe for every mesh in the app — gizmos, panels, Spark included.
 * `SkinnedMesh`, `InstancedMesh` and `BatchedMesh` keep their own `raycast`;
 * an `InstancedMesh` reaches this one per instance, which is correct for any
 * tree its geometry has.
 */
export function installBoundsTreeRaycast(): void {
  if (s_stockMeshRaycast) return;
  s_stockMeshRaycast = Mesh.prototype.raycast;
  Mesh.prototype.raycast = boundsTreeMeshRaycast;
}

function boundsTreeMeshRaycast(this: Mesh, raycaster: Raycaster, intersects: Intersection[]): void {
  const geometry = this.geometry;
  const tree = geometry.boundsTree;
  // Morph targets move the triangles away from the positions the tree was
  // built from; three's own raycast applies them.
  if (!tree || geometry.morphAttributes.position !== undefined) {
    s_stockMeshRaycast!.call(this, raycaster, intersects);
    return;
  }
  if (this.material === undefined) return;

  // Three's own early-out, kept so a ray that misses this mesh costs what it
  // did before: the tree's first step inverts the world matrix, and on an
  // assembly of thousands of parts most meshes are misses.
  if (geometry.boundingSphere === null) geometry.computeBoundingSphere();
  _sphere.copy(geometry.boundingSphere!).applyMatrix4(this.matrixWorld);
  _ray.copy(raycaster.ray).recast(raycaster.near);
  if (!_sphere.containsPoint(_ray.origin)) {
    if (_ray.intersectSphere(_sphere, _sphereHit) === null) return;
    if (_ray.origin.distanceToSquared(_sphereHit) > (raycaster.far - raycaster.near) ** 2) return;
  }
  tree.raycastObject3D(this, raycaster, intersects);
}

const _instance = new Mesh();
const _idleGeometry = _instance.geometry;
const _idleMaterial = _instance.material as Material;
const _instanceHits: Intersection[] = [];
const _batchInverse = new Matrix4();
const _batchLocalRay = new Ray();
const _range = {
  vertexStart: 0,
  vertexCount: 0,
  reservedVertexCount: 0,
  indexStart: 0,
  indexCount: 0,
  reservedIndexCount: 0,
  start: 0,
  count: 0,
};

/**
 * A `raycast` for a `BatchedMesh` whose instances were copied from source
 * geometries the caller still holds — `EntityMeshBatcher`'s batches, whose
 * source meshes stay in the scene on a layer no raycaster sees.
 *
 * Three's own batched raycast tests every triangle of each instance the ray
 * reaches, in the batch's merged buffers, where no index can be kept. This
 * raycasts each visible instance's SOURCE geometry instead — through its tree
 * when it has one — with the instance's matrix, then moves the face and vertex
 * indices into the batch's buffers, so a hit reads as three's own would:
 * `object` the batch, `batchId` the instance, `face.a/b/c` in the batch's
 * vertex space. `faceIndex` is offset the same way, but it is only a triangle's
 * POSITION in its index, and the picker may have sorted the source's index
 * after the batch copied it (`meshBoundsTreeBuilder.ts`); nothing reads it, and
 * nothing should treat it as a stable triangle id.
 *
 * Which instances it tests comes from the batch's part tree
 * (`batchPartIndex.ts`): only those whose bounds the ray crosses — or every
 * instance while the parts are moving and the tree is not yet refitted.
 *
 * `sourceGeometries[geometryId]` must hold the geometry passed to
 * `addGeometry` for that id; `instanceIds` every instance to raycast, in id
 * order (three's own order, so ties between instances sort the same).
 */
export function createBatchedMeshRaycast(
  sourceGeometries: ReadonlyArray<BufferGeometry>,
  instanceIds: ReadonlyArray<number>,
): BatchedMesh["raycast"] {
  let parts: BatchPartIndex | null = null;
  const candidates: number[] = [];
  return function batchedMeshRaycast(this: BatchedMesh, raycaster, intersects) {
    parts ??= new BatchPartIndex(this);
    let ids: ReadonlyArray<number> = instanceIds;
    if (this.matrixWorld.determinant() !== 0) {
      _batchLocalRay.copy(raycaster.ray).applyMatrix4(_batchInverse.copy(this.matrixWorld).invert());
      if (parts.collect(_batchLocalRay, candidates)) ids = candidates;
    }
    _instance.material = this.material;
    try {
      for (const instanceId of ids) {
        if (!this.getVisibleAt(instanceId)) continue;
        const geometryId = this.getGeometryIdAt(instanceId);
        const source = sourceGeometries[geometryId];
        if (!source) continue;
        this.getMatrixAt(instanceId, _instance.matrixWorld).premultiply(this.matrixWorld);
        _instance.geometry = source;
        _instance.raycast(raycaster, _instanceHits);
        if (_instanceHits.length === 0) continue;
        this.getGeometryRangeAt(geometryId, _range);
        const faceOffset = _range.start / 3;
        const vertexOffset = _range.vertexStart;
        for (const hit of _instanceHits) {
          hit.object = this;
          hit.batchId = instanceId;
          if (hit.faceIndex !== undefined && hit.faceIndex !== null) hit.faceIndex += faceOffset;
          if (hit.face) {
            hit.face.a += vertexOffset;
            hit.face.b += vertexOffset;
            hit.face.c += vertexOffset;
          }
          intersects.push(hit);
        }
        _instanceHits.length = 0;
      }
    } finally {
      _instanceHits.length = 0;
      _instance.geometry = _idleGeometry;
      _instance.material = _idleMaterial;
    }
  };
}
